import React from "react";

import { capitalize, isEmpty, map } from "lodash/fp";
import { useParams } from "react-router-dom";

import { Stack, Typography } from "@mui/material";

import { ROLE_OPTIONS } from "@app/analytics/account/constants";
import TableCell from "@app/analytics/components/Table/TableCell";
import TableRow from "@app/analytics/components/Table/TableRow";
import { User } from "@app/common/types";
import { formattedListWithConjunction } from "@app/common/utils";
import {
  EnterpriseRole,
  EnterpriseTeam,
  EnterpriseTeamUser,
  EnterpriseUser,
  EnterpriseWorkspace,
  EnterpriseWorkspaceUser,
} from "@app/common/workspaces/types";
import {
  isWorkspaceOwner,
  teamsWhereUserIsTheOnlyManager,
  userIsTheOnlyTeamManager,
} from "@app/common/workspaces/workspaceUtils";

import Avatar from "@rui/Avatar";
import Checkbox from "@rui/Checkbox";
import TooltipWrapper from "@rui/TooltipWrapper";

import MenuItem from "../MenuItem";
import OptionsMenuCell from "../OptionsMenuCell";
import RoleCell from "../RoleCell";

interface MembersTableUserRowProps {
  context: "team" | "workspace";
  currentUser: User;
  forceMenuClose: boolean;
  isSelected: boolean;
  onCheckboxClick: (userId: string, type: "user") => () => void;
  onMemberRemove: (id: string, type: "user" | "invite") => () => void;
  onRoleChange: (userId: string) => (role: EnterpriseRole) => void;
  onTeamPermissionsClick: (userId: string) => () => void;
  user: EnterpriseUser;
  canEditTeam: boolean;
  workspace: EnterpriseWorkspace;
  workspaceIsInactive: boolean;
}

const getRemoveFromWorkspaceDisabledOpts = (
  user: EnterpriseWorkspaceUser,
  workspace: EnterpriseWorkspace
) => {
  if (isWorkspaceOwner(user)) {
    return {
      disabled: true,
      tooltip: "The workspace owner cannot be removed",
    };
  }

  const teams = teamsWhereUserIsTheOnlyManager(workspace, user.userId);
  if (!isEmpty(teams)) {
    return {
      disabled: true,
      tooltip: `This user cannot be removed while they are the only manager of ${formattedListWithConjunction(
        map("name", teams)
      )}`,
    };
  }

  return { disabled: false };
};

const getRemoveFromTeamDisabledOpts = (
  user: EnterpriseTeamUser,
  workspace: EnterpriseWorkspace,
  teamId?: EnterpriseTeam["id"]
) => {
  if (userIsTheOnlyTeamManager(user.userId, workspace, teamId)) {
    return {
      disabled: true,
      tooltip:
        "A new team manager must be set before this user can be removed.",
    };
  }

  return { disabled: false };
};

const MembersTableUserRow = ({
  context,
  currentUser,
  forceMenuClose,
  isSelected,
  onCheckboxClick,
  onMemberRemove,
  onRoleChange,
  onTeamPermissionsClick,
  user,
  canEditTeam,
  workspace,
  workspaceIsInactive,
}: MembersTableUserRowProps) => {
  const roleCellDisabled =
    !canEditTeam ||
    !ROLE_OPTIONS[context].includes(user.role) ||
    user.role === "owner";

  const { teamId } = useParams();

  const canViewTeamPermissions =
    context === "workspace" && (!isEmpty(user.teams) || !workspaceIsInactive);

  const { disabled, tooltip: disabledTooltip } =
    context === "workspace"
      ? getRemoveFromWorkspaceDisabledOpts(
          user as EnterpriseWorkspaceUser,
          workspace
        )
      : getRemoveFromTeamDisabledOpts(
          user as EnterpriseTeamUser,
          workspace,
          teamId
        );

  return (
    <TableRow
      key={user.userId}
      sx={{ bgcolor: isSelected ? "states.selected" : undefined }}
    >
      {canEditTeam && (
        <TableCell padding="checkbox">
          <Checkbox
            onClick={onCheckboxClick(user.userId, "user")}
            checked={isSelected}
            inputProps={{
              "aria-labelledby": user.userId,
            }}
          />
        </TableCell>
      )}
      <TableCell>
        <Stack direction="row" alignItems="center">
          <Avatar name={user.fullName} sx={{ mr: 1 }}>
            {user.initials}
          </Avatar>
          <Stack>
            <Typography
              variant="bodyPrimaryReg"
              sx={{ color: "shades.neutral.800" }}
            >
              {user.fullName} {user.userId === currentUser.id ? " (you)" : ""}
            </Typography>
            <Typography
              variant="bodySecondaryReg"
              sx={{ color: "shades.neutral.500" }}
            >
              {user.email}
            </Typography>
          </Stack>
        </Stack>
      </TableCell>
      <RoleCell
        role={user.role as EnterpriseRole}
        options={ROLE_OPTIONS[context]}
        disabled={roleCellDisabled}
        onRoleChange={onRoleChange(user.userId)}
      />
      {context === "workspace" && (
        <TableCell>
          {isEmpty(user.teams) ? (
            "–"
          ) : (
            <Stack direction="row" alignItems="center">
              {map("name", user.teams).join(", ")}
            </Stack>
          )}
        </TableCell>
      )}
      {canEditTeam ? (
        <OptionsMenuCell
          id="detail-menu"
          menuItems={[
            ...(canViewTeamPermissions
              ? [
                  <MenuItem
                    key={`view-${context}-permissions`}
                    onClick={onTeamPermissionsClick(user.userId)}
                  >
                    View team permissions
                  </MenuItem>,
                ]
              : []),
            ...[
              <TooltipWrapper
                arrow
                key={`remove-from-${context}`}
                title={disabledTooltip}
              >
                <MenuItem
                  disabled={disabled}
                  key={`remove-from-${context}`}
                  onClick={onMemberRemove(user.userId, "user")}
                >
                  Remove from {capitalize(context)}
                </MenuItem>
              </TooltipWrapper>,
            ],
          ]}
          forceClose={forceMenuClose}
        />
      ) : (
        <TableCell />
      )}
    </TableRow>
  );
};

export default MembersTableUserRow;
